import { useMutation, useQueryClient } from '@tanstack/react-query';
import { newPasswordProblem, type MeDto, type PasswordChangedDto } from '@pl/domain';
import { type FormEvent, useId, useState } from 'react';
import { useNavigate } from 'react-router';
import { ApiError, api } from '../lib/api';
import { cachedMe, forgetMe, qk, rememberMe, useMe } from '../lib/queries';
import { ErrorNote } from '../ui/bits';
import s from './ChangePassword.module.css';

export type Surface = 'office' | 'scan';

/**
 * A password change or reset removes the person's WhatsApp links (release-2 fix M2): whoever knew the
 * old password may have linked a phone of their own.
 */
export const LINKS_REMOVED = 'WhatsApp links were removed; link again from the app.';
type FieldName = 'current' | 'next' | 'again';

function Field(props: {
  surface: Surface;
  label: string;
  autoComplete: 'current-password' | 'new-password';
  value: string;
  onChange: (v: string) => void;
  error?: string;
  hint?: string;
}) {
  const id = useId();
  const described = props.error ? `${id}-error` : props.hint ? `${id}-hint` : undefined;
  return (
    <div className="fld">
      <label htmlFor={id}>{props.label}</label>
      <input
        id={id}
        className={`inp${props.surface === 'scan' ? ' lg' : ''}${props.error ? ' err' : ''} ${s.input}`}
        type="password"
        autoComplete={props.autoComplete}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        aria-invalid={props.error ? true : undefined}
        aria-describedby={described}
      />
      {props.error ? (
        <small id={`${id}-error`} className={s.fieldError}>
          {props.error}
        </small>
      ) : (
        props.hint && (
          <small id={`${id}-hint`} className={s.hint}>
            {props.hint}
          </small>
        )
      )}
    </div>
  );
}

/**
 * The current password, the new one twice, and every refusal shown beside the field it is about.
 * The rule is checked here first (`@pl/domain`'s `newPasswordProblem`, the one the API applies), only
 * so the person hears sooner; the server decides.
 */
export function ChangePasswordForm({
  surface,
  currentLabel = 'Current password',
  submitLabel = 'Change password',
  onChanged,
}: {
  surface: Surface;
  currentLabel?: string;
  submitLabel?: string;
  /** With how many WhatsApp links of the person's the change removed, in every company. */
  onChanged: (whatsappLinksRemoved: number) => void;
}) {
  const me = useMe();
  const qc = useQueryClient();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});

  const change = useMutation({
    mutationFn: () => api.post<PasswordChangedDto>('/auth/password', { currentPassword: current, newPassword: next }),
    // Nothing typed here is kept once it is done with, in the cache or anywhere else.
    gcTime: 0,
    onSuccess: async (changed) => {
      setCurrent('');
      setNext('');
      setAgain('');
      // The server has just cleared `mustChangePassword`. Say so here at once, in the cache and in
      // the identity this device remembers, before asking again: if that refetch fails (a 5xx, no
      // signal), `RequireAuth` falls back on what it has, and what it has must not put the change
      // screen back up, asking for a temporary password that has just stopped working.
      const mine = qc.getQueryData<MeDto>(qk.me);
      if (mine) qc.setQueryData<MeDto>(qk.me, { ...mine, mustChangePassword: false });
      const remembered = cachedMe();
      if (remembered && (!mine || remembered.user.id === mine.user.id)) rememberMe({ ...remembered, mustChangePassword: false });
      onChanged(changed?.whatsappLinksRemoved ?? 0);
      await qc.invalidateQueries({ queryKey: qk.me });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'WRONG_PASSWORD') setErrors({ current: err.message });
      else if (err instanceof ApiError && err.code === 'NEW_PASSWORD_INVALID') setErrors({ next: err.message });
    },
  });
  const fieldError = change.error instanceof ApiError && (change.error.code === 'WRONG_PASSWORD' || change.error.code === 'NEW_PASSWORD_INVALID');

  const edit = (field: FieldName, set: (v: string) => void) => (v: string) => {
    set(v);
    setErrors((e) => ({ ...e, [field]: undefined }));
    if (change.error) change.reset();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const found: Partial<Record<FieldName, string>> = {};
    if (!current) found.current = 'Enter the password you have now.';
    const problem = newPasswordProblem(next, current);
    if (problem) found.next = problem;
    else if (again !== next) found.again = 'The two new passwords are not the same.';
    setErrors(found);
    if (Object.keys(found).length === 0) change.mutate();
  };

  return (
    <form className={`${s.form} ${surface === 'scan' ? s.scan : s.office}`} onSubmit={submit} noValidate>
      {/* For a password manager, so the new password is saved against the right account. */}
      <input type="text" name="username" autoComplete="username" value={me.data?.user.email ?? ''} readOnly hidden />
      <Field surface={surface} label={currentLabel} autoComplete="current-password" value={current} onChange={edit('current', setCurrent)} error={errors.current} />
      <Field surface={surface} label="New password" autoComplete="new-password" value={next} onChange={edit('next', setNext)} error={errors.next} hint="At least 12 characters." />
      <Field surface={surface} label="New password again" autoComplete="new-password" value={again} onChange={edit('again', setAgain)} error={errors.again} />
      {!fieldError && <ErrorNote error={change.error} />}
      <button type="submit" className={surface === 'scan' ? 'bigbtn accent' : `btn primary ${s.submit}`} disabled={change.isPending}>
        {submitLabel}
      </button>
    </form>
  );
}

/**
 * Account → Change password, in either shell: `/office/password` and `/scan/password`.
 *
 * Gated on `isLoadingError`, not `isError`: `me` failing to refresh while this phone still knows who
 * it is keeps the form on screen (`RefetchNote` in `bits.tsx` has the rule).
 */
export function ChangePasswordPage({ surface }: { surface: Surface }) {
  const me = useMe();
  // How many WhatsApp links the change removed; null until it is made.
  const [done, setDone] = useState<number | null>(null);
  if (me.isLoadingError)
    return (
      <div className={s.gate}>
        <ErrorNote error={me.error} />
        <button type="button" className="btn" onClick={() => void me.refetch()}>
          Try again
        </button>
      </div>
    );
  if (!me.data) return <div aria-busy="true" />;

  const body = (
    <>
      {done !== null && (
        <p role="status" className={s.done}>
          Your password is changed. Every other phone and browser you were signed in on has been signed out.
          {done > 0 && ` ${LINKS_REMOVED}`}
        </p>
      )}
      <ChangePasswordForm surface={surface} onChanged={setDone} />
    </>
  );
  if (surface === 'scan') return <div className={`scan-body ${s.scanBody}`}>{body}</div>;
  return (
    <section className={`panel ${s.panel}`} aria-label="Change password">
      <div className="panel-b">{body}</div>
    </section>
  );
}

/**
 * What someone still on a temporary password sees, in either app, instead of the app: this and
 * nothing else, until they choose their own (`me.mustChangePassword`, which the server enforces on
 * every other route). Once the change is made, `me` is asked again and `RequireAuth` carries on to
 * wherever they were going.
 */
export function ForcedPasswordChange({ surface }: { surface: Surface }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const titleId = useId();
  const signOut = useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSuccess: () => {
      forgetMe();
      qc.clear();
      nav('/login', { replace: true });
    },
  });
  return (
    <div className={s.forcedPage}>
      <main className={`${s.forcedCard} ${surface === 'scan' ? s.forcedScan : ''}`} aria-labelledby={titleId}>
        <div className={s.brand}>
          <div className="brand-mark">
            <i />
          </div>
          <div className="brand-name">
            PALLET<span>·</span>LEVEL
          </div>
        </div>
        <h1 id={titleId} className={s.forcedTitle}>
          Choose your own password
        </h1>
        <p className={s.forcedLead}>The password you signed in with was given to you by an admin, so it is only temporary. Choose one of your own to go on.</p>
        <ChangePasswordForm surface={surface} currentLabel="Temporary password" submitLabel="Save my password" onChanged={() => undefined} />
        <ErrorNote error={signOut.error} />
        <button type="button" className={s.leave} onClick={() => signOut.mutate()} disabled={signOut.isPending}>
          Sign out
        </button>
      </main>
    </div>
  );
}
